import { AuthService } from './auth.service';
import { Globals } from './../shared/globals';
import { HttpClient } from '@angular/common/http';
import { StationGroup } from '../models/station-group';
import { Station } from '../models/station.model';
import { JsonSerializer } from 'typescript-json-serializer';
import { PriceRule } from '../models/price-rule.model';
import { Injectable } from '@angular/core';
import { Permission } from '../models/permission.model';

/**
 * Service responsible for managing station groups.
 *
 * @export
 * @class StationGroupService
 */
@Injectable()
export class StationGroupService {
  private jsonSerializer: JsonSerializer;

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private globals: Globals
  ) {
    this.jsonSerializer = new JsonSerializer();
  }

  /**
   * Fetches list of station groups where the user has access to view or manage.
   *
   * @returns {Promise<StationGroup[]>}
   * @memberof StationGroupService
   */
  public getStationGroups(): Promise<StationGroup[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetStationGroupsRequest(token);
    });
  }

  public createStationGroup(
    name: string,
    description: string,
    stationIds: string[]
  ): Promise<StationGroup> {
    return this.authService.getToken().then((token) => {
      return this.performCreateStationGroupRequest(
        token,
        name,
        description,
        stationIds
      );
    });
  }

  public updateStationGroup(
    groupId: string,
    name: string,
    description: string,
    stationIds: string[]
  ): Promise<StationGroup> {
    return this.authService.getToken().then((token) => {
      return this.performUpdateStationGroupRequest(
        token,
        groupId,
        name,
        description,
        stationIds
      );
    });
  }

  public deleteStationGroup(group: StationGroup): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performDeleteStationGroupRequest(token, group.id);
    });
  }

  public getStationGroup(id: string): Promise<StationGroup> {
    return this.authService.getToken().then((token) => {
      return this.performGetStationGroupRequest(token, id);
    });
  }

  public getStationGroupPriceRules(
    stationGroupId: string
  ): Promise<PriceRule[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetStationGroupPriceRulesRequest(
        token,
        stationGroupId
      );
    });
  }

  public getPriceRulesAffectingStation(
    stationId: string
  ): Promise<PriceRule[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetPriceRulesAffectingStation(token, stationId);
    });
  }

  public deleteStationGroupPriceRules(
    stationGroupId: string,
    priceRules: PriceRule[]
  ): Promise<void> {
    const priceRuleIds = priceRules.map((p) => p.id);
    return this.authService.getToken().then((token) => {
      return this.performDeleteStationGroupPriceRulesRequest(
        token,
        stationGroupId,
        priceRuleIds
      );
    });
  }

  public createStationGroupPriceRules(
    stationGroupId: string,
    priceRules: PriceRule[]
  ): Promise<PriceRule[]> {
    return this.authService.getToken().then((token) => {
      return this.performCreateStationGroupPriceRulesRequest(
        token,
        stationGroupId,
        priceRules
      );
    });
  }

  public async checkUserPermissionsForStationGroup(
    groupId: string
  ): Promise<Permission[]> {
    const token = await this.authService.getToken();
    const permissions =
      await this.performCheckUserPermissionsForStationGroupRequest(
        token,
        groupId
      );
    return permissions;
  }

  private performGetPriceRulesAffectingStation(
    token: string,
    stationId: string
  ): Promise<PriceRule[]> {
    const params = new URLSearchParams({
      station_id: stationId,
    });
    const url = new URL(
      this.globals.apiUrl('price-rules') + '?' + params.toString()
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<PriceRule>(
              response[item],
              PriceRule
            )
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // Internal methods

  private performGetStationGroupPriceRulesRequest(
    token: string,
    stationGroupId: string
  ): Promise<PriceRule[]> {
    const params = new URLSearchParams({ station_group_id: stationGroupId });
    const url = new URL(
      this.globals.apiUrl(`price-rules?` + params.toString())
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<PriceRule>(
              response[item],
              PriceRule
            )
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performDeleteStationGroupPriceRulesRequest(
    token: string,
    stationGroupId: string,
    priceRuleIds: string[]
  ): Promise<void> {
    const url = new URL(
      this.globals.apiUrl('station-groups') +
      '/' +
      stationGroupId +
      '/price-rules/'
    );

    const options = {
      headers: this.authService.headers(token),
      body: priceRuleIds,
    };

    return new Promise((resolve, reject) => {
      this.http
        .delete(url.toString(), options)
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performCreateStationGroupPriceRulesRequest(
    token: string,
    stationGroupId: string,
    priceRules: PriceRule[]
  ): Promise<PriceRule[]> {
    const payload: any[] = [];
    for (const priceRule of priceRules) {
      const dto = {
        Id: priceRule.id,
        ProductType: priceRule.productType.toString(),
        StationGroupId: priceRule.stationGroup.id,
        UserGroupId: priceRule.userGroup.id,
        Price: priceRule.price,
        Currency: priceRule.currency,
      };
      payload.push(dto);
    }

    const url = new URL(
      this.globals.apiUrl('station-groups') +
      '/' +
      stationGroupId +
      '/price-rules/'
    );
    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<PriceRule>(
              response[item],
              PriceRule
            )
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetStationGroupsRequest(
    token: string
  ): Promise<StationGroup[]> {
    const url = new URL(this.globals.apiUrl(`stationgroups`));

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<StationGroup>(
              response[item],
              StationGroup
            )
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performCreateStationGroupRequest(
    token: string,
    name: string,
    description: string,
    stationIds: string[]
  ): Promise<StationGroup> {
    const payload = {
      Name: name,
      Description: description,
      MemberIds: stationIds,
    };

    const url = new URL(this.globals.apiUrl('stationgroups'));
    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<StationGroup>(
            response,
            StationGroup
          );
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performUpdateStationGroupRequest(
    token: string,
    groupId: string,
    name: string,
    description: string,
    stationIds: string[]
  ): Promise<StationGroup> {
    const payload = {
      Id: groupId,
      Name: name,
      Description: description,
      MemberIds: stationIds,
    };

    const url = new URL(this.globals.apiUrl('stationgroups'));
    return new Promise((resolve, reject) => {
      this.http
        .put(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<StationGroup>(
            response,
            StationGroup
          );
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performDeleteStationGroupRequest(
    token: string,
    groupId: string
  ): Promise<void> {
    const url = new URL(this.globals.apiUrl(`stationgroups`) + '/' + groupId);

    return new Promise((resolve, reject) => {
      this.http
        .delete(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetStationGroupRequest(
    token: string,
    id: string
  ): Promise<StationGroup> {
    const url = new URL(this.globals.apiUrl(`stationgroups`) + '/' + id);

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<StationGroup>(
            response,
            StationGroup
          );
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performCheckUserPermissionsForStationGroupRequest(
    token: string,
    groupId: string
  ): Promise<Permission[]> {
    const url = this.globals.apiUrl(`stationgroups/${groupId}/permissions`);
    return new Promise((resolve, reject) => {
      this.http
        .get(url, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response: any) => {
          try {
            const permissions =
              this.jsonSerializer.deserializeObjectArray<Permission>(
                response,
                Permission
              );
            resolve(permissions);
          } catch (error) {
            reject(error);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
